/**
 * Registro de componentes de preguntas
 *
 * Implementa el patrón Registry + Strategy para permitir agregar nuevos tipos
 * de preguntas sin modificar el código del QuestionRenderer
 * (Open/Closed Principle)
 */

import type { QuestionType } from '@/types/questions'
import type { Component } from 'vue'

type ComponentLoader = () => Promise<Component>

/**
 * Registro centralizado de componentes de preguntas
 */
class QuestionComponentRegistry {
  private registry = new Map<QuestionType, ComponentLoader>()

  /**
   * Registrar un componente para un tipo de pregunta
   */
  register(type: QuestionType, loader: ComponentLoader): void {
    if (this.registry.has(type)) {
      console.warn(`[QuestionComponentRegistry] Question type ${type} is already registered. Overwriting.`)
    }
    this.registry.set(type, loader)
  }

  /**
   * Cancelar el registro de un tipo de pregunta
   */
  unregister(type: QuestionType): void {
    this.registry.delete(type)
  }

  /**
   * Obtener el loader de un componente
   */
  get(type: QuestionType): ComponentLoader | null {
    return this.registry.get(type) || null
  }

  /**
   * Verificar si un tipo está registrado
   */
  has(type: QuestionType): boolean {
    return this.registry.has(type)
  }

  /**
   * Obtener todos los tipos registrados
   */
  getAllTypes(): QuestionType[] {
    return Array.from(this.registry.keys())
  }

  /**
   * Limpiar el registro (útil para testing)
   */
  clear(): void {
    this.registry.clear()
  }
}

// Singleton instance
export const questionComponentRegistry = new QuestionComponentRegistry()
